/**
 * join.tsx — Authenticated invite-redeem page.
 *
 * Lives at `/join`. The page is identical visually to `/login` (the
 * `auth-split` layout) but swaps the LoginForm for a JoinClassForm.
 * Reads the cached session that `__root.tsx`'s loader prefetched; if no
 * user is present we render an inline "Silakan masuk dulu" notice with a
 * CTA to `/login?redirect=/join` instead of hard-redirecting (so a
 * shared invite-code link reads naturally to logged-out visitors).
 *
 * Phase A's __root already centralises the "no app shell for unauthed
 * visitors" behaviour, so we don't repeat that here.
 */

import type { ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { JoinClassForm } from '../components/auth/JoinClassForm.js';
import { sessionQuery, type SessionPrincipal } from '../lib/api/queries.js';

export const Route = createFileRoute('/join')({
  loader: async ({ context }) => {
    // Warm the cache so the SessionPrincipal is available synchronously
    // for the route's pre-render — `requireSession` on the backend will
    // 401 unauthenticated callers, but the page itself still renders.
    await context.queryClient.fetchQuery(sessionQuery).catch(() => null);
    return null;
  },
  component: JoinPage,
});

function JoinPage(): ReactNode {
  const { queryClient } = Route.useRouteContext();
  const cached = queryClient.getQueryData(sessionQuery.queryKey) as
    { ok?: boolean; data?: { user?: SessionPrincipal } } | undefined;
  const user = cached?.ok ? (cached.data?.user ?? null) : null;

  return (
    <main className="page-shell" data-testid="join-page">
      <div className="auth-split">
        <aside className="auth-split-aside" aria-hidden="true">
          <img src="/images/generated/auth-desk/auth-desk.png" alt="" width={1280} height={720} />
        </aside>
        <section className="auth-split-main auth-split-card">
          <p className="page-eyebrow">Gabung kelas</p>
          <h1>Masukkan kode undangan</h1>
          <p>
            Kode undangan 6-karakter diberikan oleh tutor kamu. Setelah berhasil, kelas baru akan
            muncul di dasbor siswa kamu.
          </p>
          {user ? (
            <JoinClassForm />
          ) : (
            <div className="state-panel" role="status" data-testid="join-needs-login">
              <h2 className="state-panel-title">Silakan masuk dulu</h2>
              <p>
                Untuk gabung kelas kamu perlu masuk dulu. Setelah masuk, kembali ke halaman ini dan
                masukkan kode undangan.
              </p>
              <div className="cluster">
                <Link
                  className="button button-primary"
                  to="/login"
                  search={{ error: undefined, redirect: '/join' }}
                >
                  Masuk
                </Link>
              </div>
            </div>
          )}
          <p className="auth-return-link form-message">
            Belum punya kode? <Link to="/games">Lihat katalog game publik</Link>.
          </p>
        </section>
      </div>
    </main>
  );
}
